import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions, analytics } from '../firebase';
import { logEvent } from 'firebase/analytics';

// SweetAlert2 is loaded via CDN as a global variable
const Swal = window.Swal;

/**
 * Custom hook for OTP verification
 * Provides methods to send and verify OTP codes for order verification
 */
export function useOTPVerification() {
  const [isVerifying, setIsVerifying] = useState(false);
  const sendOtpFunction = httpsCallable(functions, 'sendOtp');
  const sendWhatsAppFunction = httpsCallable(functions, 'sendWhatsAppChatMessage');

  /**
   * Sends OTP to user's email and phone
   * @param {string} email - User's email address
   * @param {string} phone - User's phone number
   * @returns {Promise<{success: boolean, code?: string}>}
   */
  const sendOTP = async (email, phone) => {
    // Validate email before sending OTP
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const trimmedEmail = email ? String(email).trim() : '';

    // Email is optional now. If provided, must be valid.
    const isEmailProvided = trimmedEmail.length > 0;
    const isValidEmail = isEmailProvided ? emailRegex.test(trimmedEmail) : true;

    console.log('Email validation:', {
      original: email,
      trimmed: trimmedEmail,
      isProvided: isEmailProvided,
      isValid: isValidEmail,
    });

    if (isEmailProvided && !isValidEmail) {
      console.error('Email validation failed!', {
        email,
        trimmedEmail,
        type: typeof email,
      });
      await Swal.fire({
        title: '❌ Invalid Email',
        html: `<p style="font-size: 0.95rem; color: #666;">Please provide a valid email address.</p><p style="font-size: 0.85rem; color: #999;">Email received: ${email || 'undefined'
          }</p>`,
        icon: 'error',
        confirmButtonText: 'Okay',
        backdrop: `rgba(0,0,0,0.4)`,
      });
      return { success: false };
    }

    // Ensure at least phone is present if email is missing
    if (!isEmailProvided && !phone) {
      await Swal.fire({
        title: '❌ Missing Contact Info',
        text: 'Please provide at least a phone number or email.',
        icon: 'error',
      });
      return { success: false };
    }

    // Generate 6-digit OTP code
    const code = Math.floor(100000 + Math.random() * 900000).toString();
    console.log('Sending OTP to:', { email: trimmedEmail, phone, code });

    try {
      // Show loading dialog
      Swal.fire({
        title: '📧 Sending OTP...',
        html: `
          <p style="font-size: 1.05rem; color: #555;">
            ${isEmailProvided ? 'Verifying your email and sending OTP.' : 'Sending OTP via WhatsApp.'}
          </p>
        `,
        allowOutsideClick: false,
        showConfirmButton: false,
        backdrop: `rgba(0,0,0,0.4)`,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      let result;

      if (isEmailProvided) {
        // Send OTP via Email (and generic logic)
        result = await Promise.race([
          sendOtpFunction({ email: trimmedEmail, phone, code }),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('timeout')), 30000)
          ),
        ]);
      } else {
        // Send via WhatsApp directly since Email is missing
        let phoneDigits = phone.replace(/\D/g, '');
        if (phoneDigits.startsWith('0')) phoneDigits = '94' + phoneDigits.substring(1);
        const conversationId = `wa_${phoneDigits}`;

        await sendWhatsAppFunction({
          conversationId,
          text: `Your Order Verification Code is: *${code}*`,
          sender: 'system'
        });

        // Mock result format so the rest of the code works
        result = { data: { method: 'whatsapp' } };
      }

      // Log analytics event
      if (analytics) {
        logEvent(analytics, 'otp_sent', {
          method: result?.data?.method || 'unknown',
        });
      }

      Swal.close();

      // Show success message with delivery method
      const methodEmoji = {
        email: '📧',
        whatsapp: '💬',
        sms: '📱',
      };
      const methodName = {
        email: 'Email',
        whatsapp: 'WhatsApp',
        sms: 'SMS',
      };
      const deliveryMethod = result?.data?.method || 'email';

      await Swal.fire({
        title: '✅ OTP Sent!',
        html: `
          <p style="font-size: 1.05rem; color: #555;">
            ${methodEmoji[deliveryMethod]} Verification code sent via <b>${methodName[deliveryMethod]}</b>
          </p>
          <p style="font-size: 0.9rem; color: #777; margin-top: 8px;">
            Please check your ${methodName[deliveryMethod].toLowerCase()} for the 6-digit code.
          </p>
        `,
        icon: 'success',
        confirmButtonText: 'Got it!',
        backdrop: `rgba(0,0,0,0.4)`,
        timer: 3000,
        timerProgressBar: true,
      });

      return { success: true, code };
    } catch (error) {
      console.error('OTP send error:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        details: error.details,
        stack: error.stack,
      });
      Swal.close();

      const isRateLimited = error.message?.includes('Too many attempts') || error.code === 'resource-exhausted';

      await Swal.fire({
        title: isRateLimited ? '⏳ Too Many Requests' : '❌ OTP Send Failed',
        html: isRateLimited
          ? `<p style="font-size: 0.95rem; color: #666;">Please wait 15 minutes before trying again.</p>`
          : `<p style="font-size: 0.95rem; color: #666;">We couldn't send the verification code. Please check your connection and try again.</p>
             <details style="margin-top: 10px; text-align: left; font-size: 0.85rem;">
               <summary style="cursor: pointer; color: #999;">Technical Details</summary>
               <pre style="margin-top: 8px; padding: 8px; background: #f5f5f5; border-radius: 4px; overflow-x: auto;">${error.message || error.toString()}</pre>
             </details>`,
        icon: isRateLimited ? 'warning' : 'error',
        confirmButtonText: 'Okay',
        backdrop: `rgba(0,0,0,0.4)`,
      });
      return { success: false };
    }
  };

  /**
   * Prompts user to enter OTP and verifies it
   * @param {string} expectedCode - The OTP code that was sent
   * @returns {Promise<boolean>} - Returns true if verification successful
   */
  const promptAndVerifyOTP = async (expectedCode) => {
    // Admin bypass code for placing orders on behalf of customers
    const ADMIN_BYPASS_CODE = '2909165';

    const { value } = await Swal.fire({
      title: '🔐 Verify Your Order',
      html: `
        <p style="font-size: 1.05rem; margin-bottom: 8px;">
          We've sent a 6-digit verification code to your <b>email or phone</b>. 📩<br>
          Please enter it below to confirm your order.
        </p>
      `,
      input: 'text',
      inputPlaceholder: 'e.g., 123456',
      inputAttributes: {
        maxlength: 7, // Allow 7 chars for bypass code
        autocapitalize: 'off',
        autocorrect: 'off',
        style: 'text-align:center; font-size:1.1rem; letter-spacing:3px;',
      },
      imageUrl:
        'https://media.giphy.com/media/v1.Y2lkPTc5MGI3NjExa3RtNWNsYmtzZ284amt4ODc3bTd6c3Npc2YzbTJxdjlwaHNscDc3aCZlcD12MV9naWZzX3NlYXJjaCZjdD1n/12Up26fqbaU45W/giphy.gif',
      imageHeight: 200,
      confirmButtonText: 'Verify ✅',
      cancelButtonText: 'Cancel ❌',
      showCancelButton: true,
      backdrop: `rgba(0,0,0,0.4)`,
    });

    // Check if entered code matches expected OTP OR the admin bypass code
    return value === expectedCode || value === ADMIN_BYPASS_CODE;
  };

  /**
   * Complete OTP verification flow: send OTP and verify user input
   * @param {Object} params
   * @param {string} params.email - User's email address
   * @param {string} params.phone - User's phone number
   * @returns {Promise<boolean>} - Returns true if verification successful
   */
  const verifyOTP = async ({ email, phone }) => {
    setIsVerifying(true);

    try {
      // Send OTP
      const { success, code } = await sendOTP(email, phone);
      if (!success || !code) {
        setIsVerifying(false);
        return false;
      }

      // Prompt user to enter and verify OTP
      const isVerified = await promptAndVerifyOTP(code);
      setIsVerifying(false);

      if (!isVerified) {
        await Swal.fire({
          title: '❌ Verification Failed',
          html: `<p style="font-size: 0.95rem; color: #666;">The code you entered was incorrect or you cancelled the verification.</p>`,
          icon: 'error',
          confirmButtonText: 'Okay',
          backdrop: `rgba(0,0,0,0.4)`,
        });
        return false;
      }

      return true;
    } catch (error) {
      console.error('OTP verification error:', error);
      setIsVerifying(false);
      return false;
    }
  };

  return {
    verifyOTP,
    isVerifying,
  };
}
